<template>
  <div>
    <!-- Loading skeleton -->
    <div v-if="loading">
      <b-card>
        <b-skeleton width="40%" height="2rem" class="mb-2"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton width="55%" class="mb-1"/>
        <b-skeleton-table :rows="5" :columns="4" hide-header/>
      </b-card>
    </div>

    <template v-else>
      <!-- Current Score -->
      <b-row>
        <b-col md="4">
          <b-card class="text-center">
            <h6 class="text-muted">Current Health Score</h6>
            <div class="my-3">
              <span class="score-circle" :class="`bg-score-${scoreVariant}`">{{ current.score || '—' }}</span>
            </div>
            <b-badge :variant="scoreVariant" class="mb-0">{{ scoreLabel }}</b-badge>
          </b-card>
        </b-col>
        <b-col md="8">
          <b-card title="Component Breakdown">
            <div v-if="!current.components" class="text-muted">No component data.</div>
            <template v-else>
              <div v-for="(val, key) in current.components" :key="key" class="mb-2">
                <div class="d-flex justify-content-between mb-1">
                  <span class="text-capitalize">{{ key }}</span>
                  <strong>{{ val }}/100</strong>
                </div>
                <b-progress :value="val" :max="100" :variant="progressVariant(val)" height="8px" />
              </div>
            </template>
          </b-card>
        </b-col>
      </b-row>

      <!-- 30-Day History Chart -->
      <b-card title="30-Day Score History">
        <div v-if="!history.length" class="text-center py-4 text-muted">
          No history data available.
        </div>
        <vue-apex-charts
          v-else
          type="area"
          height="250"
          :options="healthChartOptions"
          :series="healthChartSeries"
        />
      </b-card>
    </template>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import VueApexCharts from 'vue-apexcharts'
import { BCard, BRow, BCol, BBadge, BSpinner, BProgress } from 'bootstrap-vue'

export default {
  name: 'FlockHealthScore',
  components: { VueApexCharts, BCard, BRow, BCol, BBadge, BSpinner, BProgress },
  data() {
    return {
      loading: false,
      current: {},
      history: [],
    }
  },
  computed: {
    healthChartDates() {
      return this.history.map(h => h.date)
    },
    healthChartScores() {
      return this.history.map(h => h.score)
    },
    healthChartOptions() {
      return {
        chart: { type: 'area', toolbar: { show: false } },
        colors: ['#28C76F'],
        xaxis: { categories: this.healthChartDates, title: { text: 'Date' } },
        yaxis: { min: 0, max: 100, title: { text: 'Health Score' } },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1 } },
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 4 },
      }
    },
    healthChartSeries() {
      return [{ name: 'Health Score', data: this.healthChartScores }]
    },
    scoreVariant() {
      const s = this.current.score || 0
      if (s >= 80) return 'success'
      if (s >= 60) return 'warning'
      if (s >= 40) return 'orange'
      return 'danger'
    },
    scoreLabel() {
      const s = this.current.score || 0
      if (s >= 80) return 'Healthy'
      if (s >= 60) return 'Fair'
      if (s >= 40) return 'At Risk'
      return 'Critical'
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      const { farmId, shedId, flockId } = this.$route.params
      this.loading = true
      try {
        const [scoreRes, histRes] = await Promise.all([
          axios.get(`web/farms/${farmId}/sheds/${shedId}/flocks/${flockId}/health-score`),
          axios.get(`web/farms/${farmId}/sheds/${shedId}/flocks/${flockId}/health-score/history`),
        ])
        this.current = scoreRes.data
        this.history = histRes.data.data || histRes.data.history || histRes.data || []
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load health score', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
    progressVariant(val) {
      if (val >= 80) return 'success'
      if (val >= 60) return 'warning'
      return 'danger'
    },
  },
}
</script>

<style scoped>
.score-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  font-size: 2rem;
  font-weight: 700;
  color: white;
}
.bg-score-success { background-color: #28c76f; }
.bg-score-warning { background-color: #ff9f43; }
.bg-score-orange  { background-color: #ff9f43; }
.bg-score-danger  { background-color: #ea5455; }
</style>
